import { useEffect, useMemo, useState } from "react"
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, type ChartConfig } from "../components/ui/chart"

/**
 * Default chart color palette — matches shadcn/ui chart tokens
 */
const CHART_COLORS = [
  "var(--color-primary)",
  "var(--color-chart-1)",
  "var(--color-chart-2)",
  "var(--color-chart-3)",
  "var(--color-chart-4)",
  "var(--color-chart-5)",
]

/**
 * Cogo JSON chart schema:
 *
 * {
 *   "type": "chart",
 *   "chartType": "area" | "line" | "bar" | "composed" | "pie" | "donut",
 *   "className": "h-[300px] w-full",
 *   "data": "#{ui.chartData}",
 *   "options": {
 *     "xAxis": { "key": "month", "hide": false },
 *     "yAxis": { "key": "volume", "hide": false, "tickFormatter": "compact" },
 *     "series": [
 *       { "key": "volume", "color": "#8884d8", "name": "Volume" },
 *       { "key": "revenue", "color": "#82ca9d", "name": "Revenue" }
 *     ],
 *     "grid": { "horizontal": true, "vertical": false, "strokeDasharray": "3 3" },
 *     "tooltip": { "show": true },
 *     "legend": { "show": false, "position": "bottom" },
 *     "gradient": true,
 *     "curved": true,
 *     "stacked": false,
 *     "innerRadius": 60,
 *     "outerRadius": 80
 *   }
 * }
 */

interface ChartOptions {
  xAxis?: { key?: string; hide?: boolean; tickFormatter?: string }
  yAxis?: { key?: string; hide?: boolean; tickFormatter?: string }
  series?: Array<{ key?: string; dataKey?: string; color?: string; fill?: string; name?: string; chartType?: "bar" | "line" | "area" }>
  grid?: { horizontal?: boolean; vertical?: boolean; strokeDasharray?: string }
  tooltip?: { show?: boolean; cursor?: boolean }
  legend?: { show?: boolean; position?: string }
  gradient?: boolean
  curved?: boolean
  stacked?: boolean
  barSize?: number
  barRadius?: [number, number, number, number]
  innerRadius?: number | string
  outerRadius?: number | string
  centerLabel?: string | { value: string; subtitle?: string }
  /** Custom color palette — overrides CHART_COLORS */
  colors?: string[]
  /** Pie/donut: explicit data key (default: first numeric key) */
  dataKey?: string
  /** Pie/donut: explicit name key (default: xKey) */
  nameKey?: string
  /** Shorthand for legend.show */
  showLegend?: boolean
  /** Shorthand for tooltip.show */
  showTooltip?: boolean
  /** Pie: stroke width */
  strokeWidth?: number
  /** Pie: show label on segments */
  showLabel?: boolean
  /** Dot radius for line/composed charts */
  dotRadius?: number
  /** Center label title font size (donut charts) */
  titleFontSize?: number
  /** Center label subtitle font size (donut charts) */
  subtitleFontSize?: number
}

/**
 * Normalize color values for ChartContainer CSS variable injection.
 * With oklch theme, CSS variables store complete color values (not raw channels),
 * so var(--chart-1) is already a valid CSS color. No wrapping needed.
 * For legacy hex values, pass through as-is.
 */
function normalizeChartColor(color: string): string {
  // Already a function call (hsl/rgb/oklch/var) — leave as-is
  if (/^(hsl|rgb|oklch|hwb|lab|lch|var)\(/.test(color)) return color
  // Hex or named color — leave as-is
  if (color.startsWith("#") || /^[a-z]+$/i.test(color)) return color
  return color
}

/**
 * Build a ChartConfig from series definitions.
 * ChartContainer injects CSS variables (--color-{key}) via <style> tag,
 * so SVG fills can use var(--color-{key}) directly.
 */
function buildChartConfig(
  seriesDefs: Array<{ key: string; color?: string; name?: string }>,
): ChartConfig {
  const config: ChartConfig = {}
  seriesDefs.forEach((s, i) => {
    const rawColor = s.color ?? CHART_COLORS[i % CHART_COLORS.length]
    config[s.key] = {
      label: s.name ?? s.key,
      color: normalizeChartColor(rawColor),
    }
  })
  return config
}

/**
 * Build the ChartContainer className — consolidate height fallback logic.
 * Recharts requires an explicit height to render; default to h-[300px]
 * only when the JSON className doesn't already specify one.
 */
function chartContainerCls(className?: string): string {
  const hasHeight = /(?:^|\s)h-/.test(className ?? "")
  const hasAspect = className?.includes("aspect-") ?? false
  return cn(!hasHeight && !hasAspect && "h-[300px]", "w-full", className)
}

function compactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
  return String(value)
}

export function ChartRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Note: legacy chart blocks overload `element.variant` as a chart-type
  // carrier (bar/line/area/pie/donut — see rawChartType below); that
  // semantic is preserved and is separate from the theme-v2 data-variant
  // contract. The theme-variant flows from atom defaults only for chart.
  const atomDefaults = useAtomDefaults("chart")
  const resolvedAtomVariant = atomDefaults.defaultVariant ?? "default"
  const resolvedAtomSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const chartAtomDataAttrs = {
    "data-atom-type": "chart" as const,
    "data-variant": resolvedAtomVariant,
    "data-size": resolvedAtomSize,
  }
  /**
   * Resolve the chart variant name.
   *
   * @deprecated since Phase 8107 (2026-05-05) — prefer `element.variant` (the
   *   SHACL canonical property in `cobo:ChartVariant`). `element.chartType` is
   *   retained as a fallback for backward-compat with legacy blocks; new
   *   blocks SHOULD use `variant`. Future phases will remove `chartType`
   *   support after corpus migration is verified empty across all paths.
   */
  const rawChartType = element.chartType ?? element.variant ?? "bar"
  const chartType =
    typeof rawChartType === "string" && rawChartType.startsWith("#{")
      ? String(resolveSymbol(rawChartType) ?? "bar")
      : rawChartType

  const opts = (element.options ?? {}) as ChartOptions
  const flatChartProperties = element as unknown as {
    activeIndex?: unknown
    hoveredSeries?: unknown
    xKey?: unknown
    xAxisKey?: unknown
    yKey?: unknown
    colors?: unknown
    config?: Record<string, { label?: string; color?: string }>
  }
  const flatXKey = typeof flatChartProperties.xKey === "string"
    ? flatChartProperties.xKey
    : typeof flatChartProperties.xAxisKey === "string"
      ? flatChartProperties.xAxisKey
      : undefined
  const flatYKey = typeof flatChartProperties.yKey === "string"
    ? flatChartProperties.yKey
    : undefined
  const flatColors = Array.isArray(flatChartProperties.colors)
    ? flatChartProperties.colors.filter((color): color is string => typeof color === "string")
    : []
  // Nested chart options remain authoritative. Canonical flat properties are
  // the fallback used by ontology-generated Cogo JSON.
  const effectiveColors = opts.colors?.length
    ? opts.colors
    : flatColors.length
      ? flatColors
      : CHART_COLORS
  const xKey = opts.xAxis?.key ?? flatXKey ?? "label"

  // Auto-detect series keys from data if no series defined
  const yKeys = useMemo(() => {
    if (opts.series && opts.series.length > 0) return opts.series.map((s) => s.key ?? s.dataKey ?? "value")
    if (opts.yAxis?.key) return [opts.yAxis.key]
    if (flatYKey) return [flatYKey]
    // Auto-detect from first data item: all numeric keys except xKey
    const firstItem = Array.isArray(element.data) ? element.data[0] : null
    if (firstItem && typeof firstItem === "object") {
      const effectiveXKey = xKey
      return Object.entries(firstItem as Record<string, unknown>)
        .filter(([k, v]) => k !== effectiveXKey && typeof v === "number")
        .map(([k]) => k)
    }
    return ["value"]
  }, [opts.series, opts.yAxis?.key, flatYKey, element.data, xKey])
  const gradient = opts.gradient !== false
  const curved = opts.curved !== false
  const stacked = opts.stacked === true
  const showTooltip = opts.showTooltip ?? opts.tooltip?.show !== false
  const showLegend = opts.showLegend ?? opts.legend?.show === true
  const showGrid = opts.grid ?? { horizontal: true, vertical: false }
  const gridDash = opts.grid?.strokeDasharray ?? "3 3"

  const data = useMemo(() => {
    if (element.data == null) return []

    if (typeof element.data === "string") {
      const resolved = resolveSymbol(element.data)
      if (Array.isArray(resolved)) return resolved as Record<string, unknown>[]
      return []
    }

    // Direct array format: [{ month: "Jan", desktop: 186 }, ...]
    if (Array.isArray(element.data)) {
      return element.data as Record<string, unknown>[]
    }

    // Chart.js format: { labels: [], datasets: [{ data: [] }] }
    const dataObj = element.data as Record<string, unknown>
    if (dataObj.labels && Array.isArray(dataObj.labels) && dataObj.datasets) {
      const labels = dataObj.labels as string[]
      const datasets = dataObj.datasets as Array<Record<string, unknown>>
      return labels.map((label, i) => {
        const row: Record<string, unknown> = { [xKey]: label }
        datasets.forEach((ds, di) => {
          const values = ds.data as number[]
          row[yKeys[di] ?? `series${di}`] = values?.[i] ?? 0
        })
        return row
      })
    }

    return []
  }, [element.data, resolveSymbol, xKey, yKeys])

  const rawActiveIndex = flatChartProperties.activeIndex
  const resolvedActiveIndex = typeof rawActiveIndex === "string"
    ? resolveSymbol(rawActiveIndex)
    : rawActiveIndex
  const numericActiveIndex = Number(resolvedActiveIndex)
  const activeIndex = resolvedActiveIndex !== null
    && resolvedActiveIndex !== undefined
    && Number.isInteger(numericActiveIndex)
    && numericActiveIndex >= 0
    && numericActiveIndex < data.length
    ? numericActiveIndex
    : undefined

  const seriesDefs = useMemo(() => {
    if (opts.series && opts.series.length > 0) {
      // Normalize: support both { key, color } and { dataKey, fill } formats
      return opts.series.map((s, i) => ({
        key: s.key ?? s.dataKey ?? `series-${i}`,
        color: s.color ?? s.fill ?? effectiveColors[i % effectiveColors.length],
        name: s.name ?? s.key ?? s.dataKey ?? `series-${i}`,
        chartType: s.chartType,
      }))
    }
    // Support element.config: { desktop: { label, color }, mobile: { label, color } }
    const config = flatChartProperties.config
    return yKeys.map((key, i) => ({
      key,
      color: config?.[key]?.color ?? effectiveColors[i % effectiveColors.length],
      name: config?.[key]?.label ?? key,
      chartType: undefined as "bar" | "line" | "area" | undefined,
    }))
  }, [opts.series, yKeys, flatChartProperties.config, effectiveColors])

  const rawHoveredSeries = flatChartProperties.hoveredSeries
  const resolvedHoveredSeries = typeof rawHoveredSeries === "string"
    && (rawHoveredSeries.includes("#{") || rawHoveredSeries.includes("@{"))
    ? resolveSymbol(rawHoveredSeries)
    : rawHoveredSeries
  const authoredHoveredSeries = typeof resolvedHoveredSeries === "string"
    && resolvedHoveredSeries.length > 0
    ? resolvedHoveredSeries
    : undefined
  const [hoveredSeries, setHoveredSeries] = useState<string | undefined>(
    authoredHoveredSeries,
  )

  useEffect(() => {
    setHoveredSeries(authoredHoveredSeries)
  }, [authoredHoveredSeries])

  const publishHoveredSeries = (series: string | undefined) => {
    setHoveredSeries(series)
    dispatchEvent(element.id, "hoverChange", {
      series: series ?? null,
      hoveredSeries: series ?? null,
      dataKey: series ?? null,
    })
  }
  const restoreAuthoredHoveredSeries = () => {
    publishHoveredSeries(authoredHoveredSeries)
  }
  const seriesOpacity = (series: string) =>
    hoveredSeries && hoveredSeries !== series ? 0.35 : 1

  // Build ChartConfig for ChartContainer — injects CSS variables via <style> tag
  const chartConfig = useMemo(() => buildChartConfig(seriesDefs), [seriesDefs])

  const yTickFormatter =
    opts.yAxis?.tickFormatter === "compact" ? compactNumber : undefined

  // X-axis tick formatter: "slice3", "slice:0:3" → first 3 chars (e.g., "January" → "Jan")
  const xTickFormatter = useMemo(() => {
    const fmt = opts.xAxis?.tickFormatter
      ?? (opts as Record<string, unknown>).tickFormatter as string | undefined
      ?? (element as unknown as Record<string, unknown>).xAxisTickFormatter as string | undefined
    if (!fmt || typeof fmt !== "string") return undefined
    if (fmt === "slice3") return (value: string) => String(value).slice(0, 3)
    // "slice:start:end" format (e.g., "slice:0:3")
    const sliceMatch = fmt.match(/^slice[:/](\d+)[:/](\d+)$/)
    if (sliceMatch) {
      const start = parseInt(sliceMatch[1])
      const end = parseInt(sliceMatch[2])
      return (value: string) => String(value).slice(start, end)
    }
    if (fmt.startsWith("slice")) {
      const n = parseInt(fmt.replace("slice", "")) || 3
      return (value: string) => String(value).slice(0, n)
    }
    return undefined
  }, [opts.xAxis?.tickFormatter, opts, (element as unknown as Record<string, unknown>).xAxisTickFormatter])

  // ─── Pie/Donut hoisted state (Rules of Hooks) ────────────────
  // These must be declared unconditionally — the `useMemo` below cannot
  // live inside the `if (chartType === "pie" || "donut")` branch or React
  // will throw a hook-order violation when other chart types render first.
  const pieNameKey = opts.nameKey ?? xKey
  const pieDataKey = opts.dataKey ?? yKeys[0] ?? "value"
  const elementConfig = flatChartProperties.config
  const pieChartConfig = useMemo(() => {
    if (chartType !== "pie" && chartType !== "donut") return {} as ChartConfig
    const cfg: ChartConfig = {}
    data.forEach((entry, i) => {
      const name = String((entry as Record<string, unknown>)[pieNameKey] ?? `slice-${i}`)
      // Prefer element.config[name].color → data.fill → data.color → palette
      const color = elementConfig?.[name]?.color
        ?? (entry as Record<string, unknown>).fill as string
        ?? (entry as Record<string, unknown>).color as string
        ?? effectiveColors[i % effectiveColors.length]
      cfg[name] = {
        label: elementConfig?.[name]?.label ?? name,
        color: normalizeChartColor(color),
      }
    })
    return cfg
  }, [chartType, data, pieNameKey, effectiveColors, elementConfig])

  const chartPropertyDataAttrs = {
    "data-chart-type": chartType,
    "data-active-index": activeIndex,
    "data-hovered-series": hoveredSeries,
    "data-x-key": xKey,
    "data-y-key": yKeys.join(","),
    "data-colors": effectiveColors.join(","),
  }

  // ─── Area Chart ───────────────────────────────────────────────
  if (chartType === "area") {
    return (
      <ChartContainer
        config={chartConfig}
        className={chartContainerCls(element.className)}
        data-testid={`chart-${element.id}`}
        {...chartAtomDataAttrs}
        {...chartPropertyDataAttrs}
      >
        <AreaChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          {gradient &&
            seriesDefs.map((s, i) => (
              <defs key={`grad-${i}`}>
                <linearGradient
                  id={`gradient-${element.id}-${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={`var(--color-${s.key})`}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={`var(--color-${s.key})`}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
            ))}
          <CartesianGrid
            horizontal={showGrid.horizontal !== false}
            vertical={showGrid.vertical === true}
            strokeDasharray={gridDash}
          />
          {!opts.xAxis?.hide && (
            <XAxis
              dataKey={xKey}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
            />
          )}
          {!opts.yAxis?.hide && (
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={yTickFormatter}
              width={48}
            />
          )}
          {showTooltip && <ChartTooltip cursor={false} content={<ChartTooltipContent />} />}
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          {seriesDefs.map((s, i) => (
            <Area
              key={s.key}
              className="cogo-chart-series"
              type={curved ? "monotone" : "linear"}
              dataKey={s.key}
              name={s.name ?? s.key}
              stroke={`var(--color-${s.key})`}
              strokeWidth={2}
              fill={
                gradient
                  ? `url(#gradient-${element.id}-${i})`
                  : `var(--color-${s.key})`
              }
              fillOpacity={gradient ? 1 : 0.15}
              stackId={stacked ? "stack" : undefined}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, fill: "white" }}
              opacity={seriesOpacity(s.key)}
              onMouseEnter={() => publishHoveredSeries(s.key)}
              onMouseLeave={restoreAuthoredHoveredSeries}
            />
          ))}
        </AreaChart>
      </ChartContainer>
    )
  }

  // ─── Composed Chart (Bar + Line + Area mix) ──────────────────
  if (chartType === "composed") {
    return (
      <ChartContainer
        config={chartConfig}
        className={chartContainerCls(element.className)}
        data-testid={`chart-${element.id}`}
        {...chartAtomDataAttrs}
        {...chartPropertyDataAttrs}
      >
        <ComposedChart data={data}>
          {gradient &&
            seriesDefs
              .filter((s) => (s.chartType ?? "bar") === "area")
              .map((s, i) => (
                <defs key={`grad-composed-${i}`}>
                  <linearGradient
                    id={`gradient-composed-${element.id}-${i}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={`var(--color-${s.key})`}
                      stopOpacity={0.1}
                    />
                    <stop
                      offset="95%"
                      stopColor={`var(--color-${s.key})`}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
              ))}
          <CartesianGrid
            horizontal={showGrid.horizontal !== false}
            vertical={showGrid.vertical === true}
            strokeDasharray={gridDash}
          />
          {!opts.xAxis?.hide && (
            <XAxis
              dataKey={xKey}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
            />
          )}
          {!opts.yAxis?.hide && (
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={yTickFormatter}
            />
          )}
          {showTooltip && <ChartTooltip cursor={false} content={<ChartTooltipContent />} />}
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          {seriesDefs.map((s, i) => {
            const seriesType = s.chartType ?? "bar"
            if (seriesType === "line") {
              return (
                <Line
                  key={s.key}
                  className="cogo-chart-series"
                  type={curved ? "monotone" : "linear"}
                  dataKey={s.key}
                  name={s.name ?? s.key}
                  stroke={`var(--color-${s.key})`}
                  strokeWidth={3}
                  dot={{ r: opts.dotRadius ?? 4, fill: `var(--color-${s.key})`, strokeWidth: 2, stroke: "var(--background)" }}
                  activeDot={{ r: 6 }}
                  opacity={seriesOpacity(s.key)}
                  onMouseEnter={() => publishHoveredSeries(s.key)}
                  onMouseLeave={restoreAuthoredHoveredSeries}
                />
              )
            }
            if (seriesType === "area") {
              const gradIdx = seriesDefs
                .filter((x) => (x.chartType ?? "bar") === "area")
                .indexOf(s)
              return (
                <Area
                  key={s.key}
                  className="cogo-chart-series"
                  type={curved ? "monotone" : "linear"}
                  dataKey={s.key}
                  name={s.name ?? s.key}
                  stroke={`var(--color-${s.key})`}
                  strokeWidth={2}
                  fill={
                    gradient
                      ? `url(#gradient-composed-${element.id}-${gradIdx})`
                      : `var(--color-${s.key})`
                  }
                  fillOpacity={gradient ? 1 : 0.15}
                  opacity={seriesOpacity(s.key)}
                  onMouseEnter={() => publishHoveredSeries(s.key)}
                  onMouseLeave={restoreAuthoredHoveredSeries}
                />
              )
            }
            // default: bar
            return (
              <Bar
                key={s.key}
                className="cogo-chart-series"
                activeIndex={activeIndex}
                dataKey={s.key}
                name={s.name ?? s.key}
                fill={`var(--color-${s.key})`}
                radius={opts.barRadius ?? 0}
                barSize={opts.barSize}
                stackId={stacked ? "stack" : undefined}
                opacity={seriesOpacity(s.key)}
                onMouseEnter={() => publishHoveredSeries(s.key)}
                onMouseLeave={restoreAuthoredHoveredSeries}
              />
            )
          })}
        </ComposedChart>
      </ChartContainer>
    )
  }

  // ─── Line Chart ───────────────────────────────────────────────
  if (chartType === "line") {
    return (
      <ChartContainer
        config={chartConfig}
        className={chartContainerCls(element.className)}
        data-testid={`chart-${element.id}`}
        {...chartAtomDataAttrs}
        {...chartPropertyDataAttrs}
      >
        <LineChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <CartesianGrid
            horizontal={showGrid.horizontal !== false}
            vertical={showGrid.vertical === true}
            strokeDasharray={gridDash}
          />
          {!opts.xAxis?.hide && (
            <XAxis
              dataKey={xKey}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
            />
          )}
          {!opts.yAxis?.hide && (
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={yTickFormatter}
              width={48}
            />
          )}
          {showTooltip && <ChartTooltip cursor={false} content={<ChartTooltipContent />} />}
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          {seriesDefs.map((s) => (
            <Line
              key={s.key}
              className="cogo-chart-series"
              type={curved ? "monotone" : "linear"}
              dataKey={s.key}
              name={s.name ?? s.key}
              stroke={`var(--color-${s.key})`}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, fill: "white" }}
              opacity={seriesOpacity(s.key)}
              onMouseEnter={() => publishHoveredSeries(s.key)}
              onMouseLeave={restoreAuthoredHoveredSeries}
            />
          ))}
        </LineChart>
      </ChartContainer>
    )
  }

  // ─── Pie / Donut Chart ────────────────────────────────────────
  if (chartType === "pie" || chartType === "donut") {
    // Pass-through: only forward props that the JSON explicitly provides.
    // Recharts handles its own defaults (auto-sized outerRadius, 0 innerRadius, etc.)
    // Note: pieNameKey, pieDataKey, elementConfig, and pieChartConfig are
    // hoisted above to comply with the Rules of Hooks.
    const pieProps: Record<string, unknown> = {}
    if (opts.outerRadius != null) pieProps.outerRadius = opts.outerRadius
    if (opts.innerRadius != null) pieProps.innerRadius = opts.innerRadius
    else if (chartType === "donut") pieProps.innerRadius = "60%"  // donut needs a hole
    if (opts.strokeWidth != null) pieProps.strokeWidth = opts.strokeWidth
    const effectiveInnerRadius = pieProps.innerRadius ?? 0

    return (
      <ChartContainer
        config={pieChartConfig}
        className={chartContainerCls(element.className)}
        data-testid={`chart-${element.id}`}
        {...chartAtomDataAttrs}
        {...chartPropertyDataAttrs}
        data-chart-inner-radius={String(effectiveInnerRadius)}
      >
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            data={data}
            dataKey={pieDataKey}
            nameKey={pieNameKey}
            cx="50%"
            cy="50%"
            {...pieProps}
          >
            {data.map((entry, i) => {
              const name = String((entry as Record<string, unknown>)[pieNameKey] ?? `slice-${i}`)
              return (
                <Cell
                  key={`cell-${i}`}
                  className="cogo-chart-series"
                  fill={`var(--color-${name})`}
                  opacity={seriesOpacity(name)}
                  onMouseEnter={() => publishHoveredSeries(name)}
                  onMouseLeave={restoreAuthoredHoveredSeries}
                />
              )
            })}
          </Pie>
          {showTooltip && <ChartTooltip content={<ChartTooltipContent nameKey={pieNameKey} />} />}
          {showLegend && <ChartLegend content={<ChartLegendContent nameKey={pieNameKey} />} />}
          {/* Center label for donut charts — auto-positioned, no LLM overlay needed */}
          {(chartType === "donut" || (opts.innerRadius != null && opts.innerRadius !== 0)) && opts.centerLabel && (
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
              <tspan x="50%" dy="-16" fontSize={opts.titleFontSize ?? 24} fontWeight="700" fill="currentColor">
                {typeof opts.centerLabel === "string" ? opts.centerLabel : (opts.centerLabel as Record<string, string>).value}
              </tspan>
              {typeof opts.centerLabel === "object" && (opts.centerLabel as Record<string, string>).subtitle && (
                <tspan x="50%" dy="20" fontSize={opts.subtitleFontSize ?? 12} fill="currentColor" opacity={0.5}>
                  {(opts.centerLabel as Record<string, string>).subtitle}
                </tspan>
              )}
            </text>
          )}
        </PieChart>
      </ChartContainer>
    )
  }

  // ─── Bar Chart (default) ──────────────────────────────────────
  return (
    <ChartContainer
      config={chartConfig}
      className={chartContainerCls(element.className)}
      data-testid={`chart-${element.id}`}
      {...chartAtomDataAttrs}
      {...chartPropertyDataAttrs}
    >
      <BarChart data={data} margin={{ left: 0, right: 0, top: 8, bottom: 0 }}>
        <CartesianGrid
          horizontal={showGrid.horizontal !== false}
          vertical={showGrid.vertical === true}
          strokeDasharray={gridDash}
        />
        {!opts.xAxis?.hide && (
          <XAxis
            dataKey={xKey}
            tickLine={false}
            tickMargin={8}
            axisLine={false}
            tickFormatter={xTickFormatter}
          />
        )}
        {!opts.yAxis?.hide && (
          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={yTickFormatter}
            width={48}
          />
        )}
        {showTooltip && <ChartTooltip cursor={opts.tooltip?.cursor === false ? false : undefined} content={<ChartTooltipContent />} />}
        {showLegend && <ChartLegend content={<ChartLegendContent />} />}
        {seriesDefs.map((s) => (
          <Bar
            key={s.key}
            className="cogo-chart-series"
            activeIndex={activeIndex}
            dataKey={s.key}
            fill={`var(--color-${s.key})`}
            radius={opts.barRadius ?? 0}
            barSize={opts.barSize}
            stackId={stacked ? "stack" : undefined}
            opacity={seriesOpacity(s.key)}
            onMouseEnter={() => publishHoveredSeries(s.key)}
            onMouseLeave={restoreAuthoredHoveredSeries}
          />
        ))}
      </BarChart>
    </ChartContainer>
  )
}
